import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from './api';
import { clearNoteDrafts, suspendNoteDrafts } from './note-drafts';
import { cachedSession, clearDevice, configureDevice, downloadAllDrafts, invalidateOfflineSession, pendingDrafts, signoutKey } from './offline-storage';
import { ensureOfflineShell, syncDevice } from './offline-sync';
import { classifyRefreshFailure, singleFlight } from './session-state';
import type { Me } from './types';

interface SessionValue {
  me: Me | null;
  loading: boolean;
  offline: boolean;
  /** The workspace could not be reached. Not the same as being signed out. */
  unreachable: boolean;
  refresh: () => Promise<void>;
  /** Resolves with a challenge when a second factor is still owed. */
  signIn: (email: string, password: string) => Promise<{ mfaChallenge?: string }>;
  completeMfa: (challenge: string, code: string) => Promise<void>;
  signOut: () => Promise<boolean>;
}

const SessionContext = createContext<SessionValue>({
  me: null,
  loading: true,
  offline: false,
  unreachable: false,
  refresh: async () => undefined,
  signIn: async () => ({}),
  completeMfa: async () => undefined,
  signOut: async () => false,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [unreachable, setUnreachable] = useState(false);

  const requestId = useRef(0);
  const lastRefresh = useRef(0);
  const supportMode = useRef(false);
  try { supportMode.current ||= localStorage.getItem('obelisk.support-session') === 'active'; } catch { /* Storage is optional. */ }
  const offlineRef = useRef(offline); offlineRef.current = offline;
  const unreachableRef = useRef(unreachable); unreachableRef.current = unreachable;
  const check = useCallback(async () => {
    lastRefresh.current = Date.now();
    const request = ++requestId.current;
    try {
      let logoutPending = false; try { logoutPending = !!localStorage.getItem(signoutKey); } catch { /* Storage is optional. */ }
      if (logoutPending) { await api.logout(); try { localStorage.removeItem(signoutKey); } catch { /* Storage unavailable. */ } }
      const next = await api.me();
      if (request !== requestId.current) return;
      supportMode.current = Boolean(next.support);
      try { if (next.support) localStorage.setItem('obelisk.support-session', 'active'); else localStorage.removeItem('obelisk.support-session'); } catch { /* Storage is optional. */ }
      configureDevice(next); setMe(next); setOffline(!navigator.onLine); setUnreachable(false);
      if (next.offline.enabled && navigator.onLine) void ensureOfflineShell().catch(() => undefined);
      void syncDevice(next);
    } catch (err) {
      if (request !== requestId.current) return;
      const cached = supportMode.current ? null : cachedSession();
      const outcome = classifyRefreshFailure(err, { hasCachedSession: Boolean(cached) });
      if (outcome.kind === 'signed-out') {
        invalidateOfflineSession(); suspendNoteDrafts(); setMe(null); setOffline(false); setUnreachable(false);
      } else if (outcome.kind === 'offline') {
        configureDevice(cached!); setMe(cached!); setOffline(true); setUnreachable(false);
      } else {
        // Keep whatever session is already in hand. A blip must not empty the
        // screen, and it must never be mistaken for signing out.
        setOffline(!navigator.onLine); setUnreachable(true);
      }
    } finally { if (request === requestId.current) setLoading(false); }
  }, []);
  const refresh = useMemo(() => singleFlight(check), [check]);

  useEffect(() => {
    void refresh();
    const lost = () => { requestId.current++; const cached = supportMode.current ? null : cachedSession(); if (supportMode.current) setMe(null); else if (cached) { configureDevice(cached); setMe(cached); } setOffline(true); setLoading(false); };
    const changed = (event: StorageEvent) => { if (event.key === signoutKey || event.key === 'obelisk.offline-session.v1') void refresh(); };
    const foreground = () => { if (document.visibilityState === 'visible' && navigator.onLine) void refresh(); };
    window.addEventListener('online', refresh); window.addEventListener('offline', lost); window.addEventListener('storage', changed);
    document.addEventListener('visibilitychange', foreground);
    const retry = window.setInterval(() => {
      const interval = supportMode.current || offlineRef.current || unreachableRef.current || pendingDrafts().some(item => item.draft.queued) ? 5000 : 60000;
      if (navigator.onLine && document.visibilityState === 'visible' && Date.now() - lastRefresh.current >= interval) void refresh();
    }, 5000);
    return () => { window.removeEventListener('online', refresh); window.removeEventListener('offline', lost); window.removeEventListener('storage', changed); document.removeEventListener('visibilitychange', foreground); window.clearInterval(retry); };
  }, [refresh]);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading,
      offline,
      unreachable,
      refresh,
      /**
       * Returns a challenge when a second factor is still owed, rather than
       * refreshing: there is no session yet, and asking `/me` would just 401.
       */
      signIn: async (email, password) => {
        const res = await api.login(email, password);
        if (res.mfaRequired && res.challenge) return { mfaChallenge: res.challenge };
        // A check that asked before this sign-in cannot answer for it.
        requestId.current++;
        await refresh();
        return {};
      },
      completeMfa: async (challenge, code) => {
        await api.loginMfa(challenge, code);
        requestId.current++;
        await refresh();
      },
      signOut: async () => {
        const pending = pendingDrafts().length;
        if (pending) {
          if (!window.confirm(`You have ${pending} unsynced draft(s). Download a copy and sign out? Signing out removes drafts and offline notes from this device. Cancel to keep working.`)) return false;
          downloadAllDrafts();
        }
        if (!offline) await api.logout();
        else localStorage.setItem(signoutKey, 'true');
        requestId.current++;
        if (me) clearDevice(me);
        clearNoteDrafts(); setMe(null);
        return true;
      },
    }),
    [me, loading, offline, unreachable, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  return useContext(SessionContext);
}

/** The membership for a department slug, or undefined when out of scope. */
export function useDeptMembership(slug: string | undefined) {
  const { me } = useSession();
  return me?.departments.find((d) => d.slug === slug);
}
